import { useEffect, useState } from 'react';

/**
 * Module-level cache so each URL is decoded at most once per page load.
 * Stores normalized Float32Array of peak amplitudes (0-1).
 */
const _cache = new Map();

/**
 * Fetch an audio (or video) URL, decode it via Web Audio API, and return a
 * Float32Array of `targetSamples` normalized peak amplitudes.
 *
 * Returns null while loading or if decoding fails (callers fall back to the
 * seeded fake waveform in that case).
 *
 * @param {string|null} url          - URL of the audio / video file to decode
 * @param {number}      targetSamples - Number of amplitude buckets to return
 */
export function useAudioWaveform(url, targetSamples = 600) {
    const [samples, setSamples] = useState(() => (url ? _cache.get(url) ?? null : null));

    useEffect(() => {
        if (!url) return;

        // Serve from cache immediately
        if (_cache.has(url)) {
            setSamples(_cache.get(url));
            return;
        }

        let cancelled = false;
        let audioCtx = null;

        (async () => {
            try {
                const resp = await fetch(url, { cache: 'force-cache' });
                if (!resp.ok || cancelled) return;

                const arrayBuf = await resp.arrayBuffer();
                if (cancelled) return;

                audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                const audioBuf = await audioCtx.decodeAudioData(arrayBuf);
                if (cancelled) return;

                // Use first channel; RMS over blocks → perceptually better than peak
                const raw = audioBuf.getChannelData(0);
                const blockSize = Math.max(1, Math.floor(raw.length / targetSamples));
                const amp = new Float32Array(targetSamples);

                for (let i = 0; i < targetSamples; i++) {
                    let sumSq = 0;
                    const start = i * blockSize;
                    for (let j = 0; j < blockSize; j++) {
                        const v = raw[start + j] || 0;
                        sumSq += v * v;
                    }
                    amp[i] = Math.sqrt(sumSq / blockSize);
                }

                // Normalize to 0-1
                let max = 0;
                for (let i = 0; i < amp.length; i++) if (amp[i] > max) max = amp[i];
                if (max > 0) for (let i = 0; i < amp.length; i++) amp[i] /= max;

                _cache.set(url, amp);
                if (!cancelled) setSamples(amp);
            } catch {
                // CORS, unsupported codec, network error → fall back to fake waveform silently
            } finally {
                audioCtx?.close();
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [url, targetSamples]);

    return samples;
}
